import { Link } from "react-router-dom";
import { X, Minus, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/data/products";

export default function CartDrawer() {
  const { isOpen, closeCart, items, subtotal, removeItem, setQuantity } = useCart();

  return (
    <>
      {/* Overlay */}
      <div
        onClick={closeCart}
        data-testid="cart-overlay"
        className={`fixed inset-0 z-50 bg-[#2A2626]/40 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      {/* Drawer */}
      <aside
        data-testid="cart-drawer"
        aria-hidden={!isOpen}
        className={`fixed top-0 right-0 z-[60] h-full w-full sm:w-[440px] bg-[#FAF8F5] shadow-2xl flex flex-col transform transition-transform duration-500 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 h-20 border-b border-[#DCD5CA]">
          <p className="overline">Your Bag ({items.length})</p>
          <button
            data-testid="cart-close-button"
            onClick={closeCart}
            aria-label="Close cart"
            className="p-2 -mr-2 text-[#2A2626] hover:opacity-70"
          >
            <X strokeWidth={1.5} size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {items.length === 0 ? (
            <div
              data-testid="cart-empty-state"
              className="h-full flex flex-col items-center justify-center text-center py-24"
            >
              <p className="font-serif text-2xl text-[#2A2626]">Your bag is quiet.</p>
              <p className="mt-3 text-sm text-[#5C564F] max-w-[260px] leading-relaxed">
                Discover pieces handcrafted with semi-precious crystals.
              </p>
              <Link
                to="/shop"
                onClick={closeCart}
                data-testid="cart-empty-shop-link"
                className="mt-8 inline-block bg-[#2A2626] text-[#FAF8F5] px-8 py-3 text-[11px] uppercase tracking-[0.24em] hover:bg-[#3A3632] transition-colors"
              >
                Browse Earrings
              </Link>
            </div>
          ) : (
            <ul className="space-y-6">
              {items.map((item) => (
                <li key={item.id} data-testid={`cart-item-${item.slug}`} className="flex gap-4">
                  <Link
                    to={`/product/${item.slug}`}
                    onClick={closeCart}
                    className="w-24 h-28 bg-[#F0EBE1] shrink-0 overflow-hidden"
                  >
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <Link
                          to={`/product/${item.slug}`}
                          onClick={closeCart}
                          className="font-serif text-lg text-[#2A2626] leading-tight"
                        >
                          {item.name}
                        </Link>
                        <p className="mt-1 text-sm text-[#5C564F]">{formatINR(item.price)}</p>
                      </div>
                      <button
                        data-testid={`cart-remove-${item.slug}`}
                        onClick={() => removeItem(item.id)}
                        className="text-[11px] uppercase tracking-[0.18em] text-[#5C564F] hover:text-[#2A2626]"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="mt-auto flex items-center border border-[#DCD5CA] w-fit">
                      <button
                        data-testid={`cart-qty-decrease-${item.slug}`}
                        onClick={() => setQuantity(item.id, item.quantity - 1)}
                        className="p-2 hover:bg-[#F0EBE1]"
                        aria-label="Decrease quantity"
                      >
                        <Minus size={13} strokeWidth={1.5} />
                      </button>
                      <span className="px-4 text-sm font-sans">{item.quantity}</span>
                      <button
                        data-testid={`cart-qty-increase-${item.slug}`}
                        onClick={() => setQuantity(item.id, item.quantity + 1)}
                        className="p-2 hover:bg-[#F0EBE1]"
                        aria-label="Increase quantity"
                      >
                        <Plus size={13} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#DCD5CA] px-6 py-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="overline">Subtotal</span>
              <span data-testid="cart-subtotal" className="font-serif text-2xl text-[#2A2626]">
                {formatINR(subtotal)}
              </span>
            </div>
            <p className="text-[12px] text-[#5C564F]">
              Taxes and shipping calculated at checkout. Complimentary shipping over ₹4,000.
            </p>
            <Link
              to="/checkout"
              onClick={closeCart}
              data-testid="drawer-checkout-button"
              className="block text-center w-full bg-[#2A2626] text-[#FAF8F5] py-4 text-[11px] uppercase tracking-[0.24em] hover:bg-[#3A3632] transition-colors"
            >
              Checkout · Pay Online
            </Link>
            <Link
              to="/cart"
              onClick={closeCart}
              className="mt-3 block text-center w-full py-2 text-[11px] uppercase tracking-[0.22em] text-[#2A2626] link-underline"
            >
              View bag
            </Link>
          </div>
        )}
      </aside>
    </>
  );
}
